import { Spinner } from '@cimplico/components';
import { Link } from 'react-router-dom';
import { Worksheet, WorksheetStatusEnum } from '../../types/worksheet';
import { useWorksheets } from '../../queries/use-worksheets';
import { useUserContext } from '../../providers/UserProvider';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import SummaryTableTitle from './SummaryTableTitle';
import { WorksheetStatusIcon } from '../worksheet-v2/WorksheetStatusIcon';
import { isNullOrUndefined } from '../new-workpaper/utils';

function WorksheetsAssigned(): JSX.Element {
    const { id: userId } = useUserContext();
    const worksheetStatus = [WorksheetStatusEnum.UNPREPARED, WorksheetStatusEnum.IN_PROGRESS, WorksheetStatusEnum.AWAITING_REVIEW];

    const worksheets = useWorksheets({
        page: 1,
        filterQuery: `assigned=${userId}&status=${worksheetStatus.join('&status=')}`,
        searchQuery: '',
        sortQuery: 'sort[dir]=asc&sort[field]=name',
    });

    return (
        <div>
            <div className="border-b pb-3">
                <SummaryTableTitle title="Assigned" count={worksheets.data ? (worksheets.data.totalItems as number).toString() : '0'} />
            </div>
            <div className="relative">
                {worksheets.isLoading && (
                    <div className="flex w-full justify-center pt-4">
                        <Spinner size="sm" />
                    </div>
                )}
                {worksheets.isError && <p className="block text-center text-sm text-gray-500 pt-4">Unable to get worksheets.</p>}
                {worksheets.isFetching && !worksheets.isLoading && (
                    <div className="absolute top-2 left-0 h-full w-full bg-white/30 flex items-center justify-center">
                        <Spinner size="sm" />
                    </div>
                )}
                {worksheets.isSuccess && (
                    <div className="max-h-[350px] overflow-y-auto ">
                        {worksheets.data.totalItems === 0 ? (
                            <div className="mt-12">
                                <EmptyStateNew
                                    icon={<UserPlusIcon />}
                                    title="No assigned worksheets"
                                    titleSize="small"
                                    helpText="See any incomplete worksheets you're assigned to."
                                    helpLink="https://learn.cimplico.com/docs/dashboard-overview#tasks-and-efficiency"
                                />
                            </div>
                        ) : (
                            <div className="mt-2">
                                {worksheets.data.worksheets.map((w) => {
                                    return <WorksheetRow key={w.id} worksheet={w} />;
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

type WorksheetRowProps = {
    worksheet: Worksheet;
};

function WorksheetRow({ worksheet }: WorksheetRowProps): JSX.Element {
    return (
        <div className="flex items-center gap-2 p-2 border-b">
            <WorksheetStatusIcon status={worksheet.status} />
            <Link to={`../${isNullOrUndefined(worksheet.account) ? 'other-schedules' : 'trial-balance'}/${worksheet.id}`}>
                <p className="text-indigo-600 dark:text-indigo-400 text-sm cursor-pointer pl-2">{worksheet.name}</p>
            </Link>
        </div>
    );
}

export default WorksheetsAssigned;
